import { useState, useEffect, useCallback } from 'react';
import { Button } from './Button';

interface StartingCountdownProps {
  /** Countdown start value in seconds */
  countdownSeconds: number;
  /** ISO timestamp when countdown started (for sync) */
  startingAt: string;
  /** Called when countdown reaches zero */
  onCountdownComplete: () => void;
  /** Called when user clicks "Wait I'm Not Ready!" */
  onCancel: () => Promise<void>;
  /** Whether cancel is in progress */
  isCancelling?: boolean;
}

export function StartingCountdown({
  countdownSeconds,
  startingAt,
  onCountdownComplete,
  onCancel,
  isCancelling = false,
}: StartingCountdownProps) {
  const [remaining, setRemaining] = useState(() => {
    // Calculate remaining time based on server timestamp
    const startTime = new Date(startingAt).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - startTime) / 1000);
    return Math.max(0, countdownSeconds - elapsed);
  });

  useEffect(() => {
    if (remaining <= 0) {
      onCountdownComplete();
      return;
    }

    const timer = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(timer);
          onCountdownComplete();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remaining, onCountdownComplete]);

  const handleCancel = useCallback(async () => {
    if (!isCancelling) {
      await onCancel();
    }
  }, [onCancel, isCancelling]);

  // Calculate progress for the circular timer
  const progress = remaining / countdownSeconds;
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-8 p-8">
        {/* Title */}
        <h2 className="text-2xl font-bold text-white text-center">
          Hunt Starting!
        </h2>

        {/* Circular countdown timer */}
        <div className="relative w-32 h-32">
          {/* Background circle */}
          <svg className="w-full h-full -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="45"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="8"
            />
            {/* Progress circle */}
            <circle
              cx="64"
              cy="64"
              r="45"
              fill="none"
              stroke="#10b981"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-linear"
            />
          </svg>
          {/* Number in center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl font-bold text-white tabular-nums">
              {remaining}
            </span>
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-lg text-white/80 text-center">
          Get ready to hunt!
        </p>

        {/* Cancel button */}
        <Button
          variant="secondary"
          size="lg"
          onClick={handleCancel}
          isLoading={isCancelling}
          disabled={isCancelling || remaining <= 0}
          className="min-w-64"
        >
          {isCancelling ? 'Cancelling...' : "Wait I'm Not Ready!"}
        </Button>
      </div>
    </div>
  );
}
